import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';
import useIsAdmin from '../hooks/useIsAdmin';

const API = process.env.REACT_APP_BACKEND_URL;

// key (original text) -> replacement text
let overrides = new Map();
let loaded = false;

const isPureTextLeaf = (el) => {
  if (!el || !el.childNodes || el.childNodes.length === 0) return false;
  let hasText = false;
  for (const n of el.childNodes) {
    if (n.nodeType === Node.TEXT_NODE) {
      if (n.nodeValue && n.nodeValue.trim()) hasText = true;
    } else {
      return false;
    }
  }
  return hasText;
};

const applyOverrides = (root) => {
  if (!overrides.size || !root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => {
      const p = n.parentElement;
      if (!p) return NodeFilter.FILTER_REJECT;
      const tag = p.tagName;
      if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'TEXTAREA') return NodeFilter.FILTER_REJECT;
      if (p.isContentEditable) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node;
  while ((node = walker.nextNode())) {
    const raw = node.nodeValue;
    if (!raw) continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const repl = overrides.get(trimmed);
    if (repl !== undefined && repl !== trimmed) {
      node.nodeValue = raw.replace(trimmed, repl);
    }
  }
};

const loadOverrides = async () => {
  if (loaded) return;
  try {
    const res = await fetch(`${API}/api/text-overrides`);
    if (res.ok) {
      const data = await res.json();
      overrides = new Map(Object.entries(data.overrides || {}));
    }
  } catch { /* offline — skip */ }
  loaded = true;
};

const saveOverride = async (key, text) => {
  const res = await fetch(`${API}/api/admin/text-overrides`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, text }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

const startEditing = (el) => {
  const displayed = el.textContent.trim();
  if (!displayed) return;
  // If this text is itself an override result, key by the ORIGINAL text so
  // we update the existing entry instead of chaining overrides.
  let key = displayed;
  for (const [k, v] of overrides.entries()) {
    if (v === displayed) { key = k; break; }
  }
  const prevOutline = el.style.outline;
  const prevOffset = el.style.outlineOffset;
  el.setAttribute('contenteditable', 'plaintext-only');
  if (!el.isContentEditable) el.setAttribute('contenteditable', 'true');
  el.style.outline = '2px dashed #9a7b24';
  el.style.outlineOffset = '2px';
  el.setAttribute('data-testid', 'admin-text-editing');
  el.focus();
  try {
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  } catch { /* selection best-effort */ }

  let finished = false;
  const finish = async (commit) => {
    if (finished) return;
    finished = true;
    el.removeEventListener('blur', onBlur);
    el.removeEventListener('keydown', onKey);
    el.removeAttribute('contenteditable');
    el.removeAttribute('data-testid');
    el.style.outline = prevOutline;
    el.style.outlineOffset = prevOffset;
    const next = el.textContent.trim();
    if (!commit || next === displayed) {
      el.textContent = displayed;
      return;
    }
    try {
      const resp = await saveOverride(key, next);
      if (resp.deleted) {
        overrides.delete(key);
        el.textContent = key;
        toast.success('Text restored to original');
      } else {
        overrides.set(key, next);
        el.textContent = next;
        toast.success('Text updated everywhere');
      }
      applyOverrides(document.body);
    } catch (e) {
      el.textContent = displayed;
      toast.error(`Save failed: ${(e.message || '').slice(0, 80)}`);
    }
  };
  const onBlur = () => finish(true);
  const onKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); el.blur(); }
    if (e.key === 'Escape') { e.preventDefault(); finish(false); }
  };
  el.addEventListener('blur', onBlur);
  el.addEventListener('keydown', onKey);
};

/**
 * Admin-only, mounted once in App:
 *  - Applies saved text overrides to every page (MutationObserver keeps up
 *    with route changes / lazy content) for ALL visitors.
 *  - For admins: Ctrl+Click (Cmd+Click on Mac) any text to edit it inline —
 *    OR tap the floating "Edit text" pill to enter edit mode, where a plain
 *    tap/click on any text edits it (mobile-friendly, no keyboard modifier).
 *    Enter/blur saves, Escape cancels, saving the original text deletes the
 *    override.
 */
export default function AdminTextEditor() {
  const isAdmin = useIsAdmin();
  const [editMode, setEditMode] = useState(false);
  const editModeRef = useRef(false);
  editModeRef.current = editMode;

  useEffect(() => {
    let observer;
    let timer = null;
    loadOverrides().then(() => {
      applyOverrides(document.body);
      observer = new MutationObserver(() => {
        if (timer) return;
        timer = setTimeout(() => {
          timer = null;
          applyOverrides(document.body);
        }, 250);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    });
    return () => {
      if (observer) observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (!isAdmin) return undefined;
    const onClick = (e) => {
      if (!(e.ctrlKey || e.metaKey || editModeRef.current)) return;
      const el = e.target;
      if (!el || el.closest('input, textarea, select, [contenteditable="true"], [contenteditable="plaintext-only"], [data-admin-edit-ui]')) return;
      // Walk up a couple of levels to find the nearest pure-text element
      // (clicking a <span> inside a button should edit the span).
      let target = el;
      for (let i = 0; i < 3 && target; i += 1) {
        if (isPureTextLeaf(target)) break;
        target = target.parentElement;
      }
      if (!target || !isPureTextLeaf(target)) return;
      e.preventDefault();
      e.stopPropagation();
      startEditing(target);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isAdmin]);

  if (!isAdmin) return null;
  return (
    <button
      type="button"
      data-admin-edit-ui="true"
      onClick={() => {
        setEditMode((m) => {
          const next = !m;
          toast[next ? 'success' : 'info'](
            next ? 'Edit mode ON — tap any text to change it' : 'Edit mode off',
            { duration: 2500 },
          );
          return next;
        });
      }}
      className={`fixed bottom-24 sm:bottom-6 left-3 z-[70] flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold shadow-lg border transition-colors ${
        editMode
          ? 'bg-[#9a7b24] text-white border-[#7d6119] animate-pulse'
          : 'bg-white/95 text-[#7d6119] border-[#9a7b24]/50 hover:bg-[#9a7b24]/10'
      }`}
      aria-pressed={editMode}
      title={editMode ? 'Tap any text to edit it. Tap here again to finish.' : 'Turn on text editing'}
      data-testid="admin-edit-mode-toggle"
    >
      <Pencil className="w-3.5 h-3.5" />
      {editMode ? 'Editing — tap text' : 'Edit text'}
    </button>
  );
}
